import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';

/**
 * Removes the OIDC bearer token from requests to public endpoints, and redirects on 401/403.
 *
 * `angular-oauth2-oidc` attaches the token by URL prefix (`resourceServer.allowedUrls` in
 * AppModule) and that mechanism cannot express an exclusion. Public form endpoints are
 * reCAPTCHA-protected and deliberately refuse to downgrade: a present-but-invalid token is
 * rejected outright instead of falling back to the captcha path. An expired token left over
 * from an idle session therefore turned a perfectly valid public submission into a rejection
 * (SDDS-2723). Those endpoints never consume the token, so it is stripped here, after the
 * library's own interceptor has added it.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /**
   * Requests whose URL contains this segment must never carry an Authorization header.
   * Matched without a leading slash: URLs are built as `environment.apiUrl + 'api/v1/public/...'`
   * and `apiUrl` is absolute in every deployed configuration but empty in the default
   * `environment.ts`, which would otherwise produce a relative URL that never matches.
   */
  private static readonly PUBLIC_API_SEGMENT = 'api/v1/public/';

  constructor(
    private readonly _router: Router
  ) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    return next.handle(this.stripTokenFromPublicRequest(req)).pipe(
      tap(
        () => { },
        error => {
          const respError = error as HttpErrorResponse;
          if ( respError && (respError.status === 401 || respError.status === 403) ) {
            this._router.navigate(['/page-not-found']);
          }
        }
      )
    );
  }

  private stripTokenFromPublicRequest(req: HttpRequest<any>): HttpRequest<any> {
    if (!req.url.includes(AuthInterceptor.PUBLIC_API_SEGMENT) || !req.headers.has('Authorization')) {
      return req;
    }

    return req.clone({ headers: req.headers.delete('Authorization') });
  }
}